// Normalised idea bank for all topics.
// Each topic has sections[] used by both the IdeaPractice component and the HomeScreen card preview.
// color values: 'green' | 'red' | 'blue' | 'orange' | 'purple' | 'teal' | 'amber'

export const SECTION_COLORS = {
  green:  { bg:'#f0faf4', border:'#bbf7d0', label:'#15803d', text:'#14532d' },
  red:    { bg:'#fff5f5', border:'#fecaca', label:'#dc2626', text:'#7f1d1d' },
  blue:   { bg:'#eff6ff', border:'#bfdbfe', label:'#1d4ed8', text:'#1e3a8a' },
  orange: { bg:'#fff7ed', border:'#fed7aa', label:'#c2410c', text:'#7c2d12' },
  purple: { bg:'#faf5ff', border:'#e9d5ff', label:'#7c3aed', text:'#4c1d95' },
  teal:   { bg:'#f0fdfa', border:'#99f6e4', label:'#0f766e', text:'#134e4a' },
  amber:  { bg:'#fffbeb', border:'#fde68a', label:'#b45309', text:'#78350f' },
};

export const TOPIC_IDEAS = {

  'rewarding-students': {
    topic: 'Education & Student Rewards',
    task: 'Write ONE body paragraph arguing that students who show progress and improvement deserve more recognition than those who simply achieve the highest scores.',
    trialFree: false,
    sections: [
      { label: 'Arguments For Rewarding Progress', color: 'green', items: [
        'Progress-based rewards acknowledge individual starting points and diverse learning journeys',
        'All students — not just top performers — feel motivated when improvement is recognised',
        'Growth mindset research shows that rewarding effort leads to longer-term academic success',
        'More inclusive systems reduce dropout rates and improve school-wide outcomes',
      ]},
      { label: 'Arguments For Rewarding High Scores', color: 'red', items: [
        'High scores reflect sustained discipline and consistent effort throughout the year',
        'Merit-based rewards motivate healthy academic competition among students',
        'Top performers inspire peers and raise the overall standard of the class',
        'Scholarships for excellence incentivise hard work across all students',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Finland: education focuses on individual progress, not national rankings — top PISA scores',
        'Jordan: schools using progress tracking reported higher student retention rates',
        'Egypt: top scorers receive government scholarships to top universities',
        'Singapore: national exam scores directly determine career and university opportunities',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'merit-based', 'intrinsic motivation', 'academic excellence', 'inclusive education',
        'equitable recognition', 'holistic assessment', 'growth mindset', 'formative assessment',
      ]},
    ],
  },

  'university-vs-work': {
    topic: 'University Degree vs. Work Experience',
    task: 'Write ONE body paragraph arguing that practical work experience and soft skills are more valuable to employers than a university degree.',
    trialFree: false,
    sections: [
      { label: 'Arguments For Work Experience', color: 'green', items: [
        'Employers increasingly value communication, teamwork, adaptability, and problem-solving over grades',
        'Work experience demonstrates real ability — not just theoretical knowledge',
        'Many industries (technology, marketing, sales) now hire based on portfolios and skills tests',
        'Soft skills are difficult to teach in classrooms but essential in every workplace',
      ]},
      { label: 'Arguments For University Degrees', color: 'red', items: [
        'Degrees signal to employers that candidates have theoretical knowledge and academic discipline',
        'Regulated professions like medicine, law, and engineering legally require formal qualifications',
        'University develops critical thinking, research skills, and subject expertise',
        'Graduates often earn significantly more over their lifetime than non-graduates',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Google, Apple, IBM: removed degree requirements for many roles as of 2022–2024',
        'UAE: tech and marketing companies hire based on proven skills, not degrees',
        'LinkedIn data (2024): soft skills listed as top hiring factor by 92% of recruiters',
        'Germany: apprenticeship system produces highly skilled workers without university',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'soft skills', 'vocational training', 'employability', 'degree premium',
        'portfolio-based hiring', 'skills mismatch', 'practical competencies', 'lifelong learning',
      ]},
    ],
  },

  'city-vs-countryside': {
    topic: 'City vs. Countryside Living',
    task: 'Write ONE body paragraph explaining the main advantages of living in the countryside compared to city life.',
    trialFree: false,
    sections: [
      { label: 'Advantages of Countryside', color: 'green', items: [
        'Cleaner air, green spaces, and a slower, less stressful pace of life',
        'Countryside living is associated with stronger community bonds and better mental health',
        'Lower cost of living makes rural life financially sustainable for many families',
        'Remote working trends now allow rural residents to access urban-level careers from home',
      ]},
      { label: 'Advantages of City Living', color: 'blue', items: [
        'Superior access to employment, education, healthcare, and cultural life',
        'Urban infrastructure — hospitals, universities, transport — is concentrated in cities',
        'Greater social diversity and networking opportunities in urban environments',
        'Higher earning potential and career advancement available in major cities',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Scandinavian countries: rural populations have high wellbeing and life satisfaction scores',
        'Post-COVID: thousands relocated from cities to countryside without losing careers',
        'Dubai and Riyadh: world-class opportunities concentrated in cities',
        'Cairo: extreme traffic and pollution drive many to seek rural alternatives',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'urbanisation', 'rural-urban migration', 'quality of life', 'cost of living',
        'community cohesion', 'green spaces', 'commuter culture', 'digital nomad',
      ]},
    ],
  },

  'traditional-vs-digital-advertising': {
    topic: 'Traditional vs. Digital Advertising',
    task: 'Write ONE body paragraph arguing that digital advertising is more effective and beneficial for businesses than traditional advertising methods.',
    trialFree: false,
    sections: [
      { label: 'Advantages of Digital Advertising', color: 'green', items: [
        'Digital advertising allows precise targeting by age, location, interests, and purchasing behaviour',
        'Real-time analytics show exactly what is working — advertisers can adjust campaigns instantly',
        'Much lower cost per impression than TV or print — accessible to small businesses',
        'Interactive formats drive immediate consumer action and measurable results',
      ]},
      { label: 'Advantages of Traditional Advertising', color: 'blue', items: [
        'TV and print reaches mass audiences simultaneously, including non-internet users',
        'Builds brand credibility and trust through long-form storytelling',
        'Older demographics and rural populations better reached through TV and print',
        'High-production TV commercials create emotional impact and lasting brand memory',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Digital: now accounts for over 60% of total ad spending in Europe and North America',
        'Super Bowl: $7 million for 30 seconds — traditional still powerful for mass brands',
        'Egyptian SMEs: WhatsApp and Instagram marketing has replaced expensive print campaigns',
        'Meta and Google ads: allow targeting by postcode, job title, purchasing behaviour',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'target audience', 'return on investment', 'impressions', 'conversion rate',
        'programmatic advertising', 'content marketing', 'brand awareness', 'analytics',
      ]},
    ],
  },

  'government-vs-individual-health': {
    topic: 'Government vs. Individual Responsibility for Health',
    task: 'Write ONE body paragraph arguing that governments bear greater responsibility for ensuring healthy lifestyles than individuals themselves.',
    trialFree: false,
    sections: [
      { label: 'Government Responsibility', color: 'green', items: [
        'Governments can regulate food labelling, ban harmful substances, and fund public health campaigns',
        'Structural factors like poverty prevent individuals from making healthy choices independently',
        'Public health infrastructure — free healthcare, safe parks — enables healthy living for all',
        'Government intervention has historically eradicated diseases and extended life expectancy',
      ]},
      { label: 'Individual Responsibility', color: 'blue', items: [
        'Adults have the knowledge and freedom to make informed choices about diet and exercise',
        'Personal responsibility promotes self-discipline and long-term lifestyle change',
        'Many healthy choices require no government support — walking, cooking, sleeping well',
        'Government resources are limited — individuals must take initiative for their own wellbeing',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'UK sugar tax (2018): reduced sugar in drinks by 28% — individuals alone could not achieve this',
        'Norway: government subsidises healthy food and taxes unhealthy products',
        'Japan: individual dietary culture explains among the world\'s lowest obesity rates',
        'Singapore: mandatory school health programmes run by national promotion board',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'public health', 'individual freedom', 'nanny state', 'preventive medicine',
        'health promotion', 'lifestyle choices', 'health inequality', 'food deserts',
      ]},
    ],
  },

  'competitive-vs-cooperative': {
    topic: 'Competitive vs. Cooperative Workplaces',
    task: 'Write ONE body paragraph arguing that cooperative workplace cultures produce better results than competitive environments.',
    trialFree: false,
    sections: [
      { label: 'Arguments For Cooperation', color: 'green', items: [
        'Cooperation produces better outcomes for complex, creative, and long-term projects',
        'Teams that trust each other share knowledge, solve problems faster, and innovate more',
        'Collaborative cultures have lower turnover, higher satisfaction, and better wellbeing',
        'Most modern business challenges require cross-functional teamwork, not individual competition',
      ]},
      { label: 'Arguments For Competition', color: 'red', items: [
        'Competition drives employees to perform at their highest level and exceed targets',
        'Competitive environments attract ambitious, high-performing talent',
        'Performance-based bonuses create clear incentives for excellence',
        'Healthy competition can foster innovation as employees seek new solutions',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Google Project Aristotle: psychological safety — not individual talent — predicts team success',
        'Amazon competitive culture: drove rapid growth but also high staff turnover and burnout',
        'Toyota lean manufacturing: cooperative problem-solving drives continuous improvement',
        'Scandinavian companies: flat hierarchies and cooperation produce world-leading productivity',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'team cohesion', 'psychological safety', 'knowledge sharing', 'burnout',
        'performance metrics', 'collaborative environment', 'workplace culture', 'turnover',
      ]},
    ],
  },

  'technology-in-schools': {
    topic: 'Technology in Education',
    task: 'Write ONE body paragraph explaining the main advantages of using technology in every classroom.',
    trialFree: false,
    sections: [
      { label: 'Advantages of Technology in Schools', color: 'green', items: [
        'Technology enables personalised, self-paced learning for every student',
        'Interactive tools make abstract concepts tangible and engaging',
        'Digital access gives students in remote areas equal educational opportunities',
        'Technology prepares students for a workforce that is entirely digitally dependent',
      ]},
      { label: 'Counter-Arguments', color: 'red', items: [
        'Technology can distract students — social media and gaming are constant temptations',
        'Over-reliance on screens may harm children\'s cognitive development and attention spans',
        'Not all schools can afford devices — risks widening educational inequality',
        'Effective teaching still requires human connection and mentorship technology cannot replace',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Estonia: national digital education programme — one of the world\'s most successful',
        'South Korea: all textbooks replaced with digital versions in primary schools',
        'UAE: smart classrooms with AI-assisted learning in government schools',
        'Khan Academy: free technology-based learning used by 135 million students globally',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'digital literacy', 'e-learning', 'blended learning', 'screen time',
        'personalised learning', 'digital divide', 'educational technology', 'AI tutoring',
      ]},
    ],
  },

  'zoos-and-animals': {
    topic: 'Animal Rights — Zoos',
    task: 'Write ONE body paragraph arguing that keeping animals in zoos is cruel and that zoos should be replaced by wildlife sanctuaries.',
    trialFree: false,
    sections: [
      { label: 'Arguments Against Zoos', color: 'red', items: [
        'Zoo animals are kept in artificial environments that prevent natural behaviours',
        'Captivity causes psychological distress in highly intelligent species like elephants',
        'The primary purpose of many zoos is entertainment and profit, not conservation',
        'Wildlife sanctuaries and national parks allow animals to live in near-natural conditions',
      ]},
      { label: 'Arguments For Zoos', color: 'green', items: [
        'Zoos play a vital role in breeding programmes for critically endangered species',
        'Many animals in zoos were born in captivity and cannot survive in the wild',
        'Modern accredited zoos fund and conduct serious field conservation research',
        'Zoos educate millions of children about species they would never encounter in nature',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'WWF: documents psychological damage and stress in captive elephants',
        'San Diego Zoo: one of the most successful conservation breeding programmes globally',
        'Giant pandas: zoos have played a critical role in preventing their extinction',
        'Born Free Foundation: campaigns for release of zoo animals to sanctuaries',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'conservation', 'captivity', 'endangered species', 'biodiversity',
        'wildlife sanctuary', 'natural habitat', 'breeding programme', 'animal welfare',
      ]},
    ],
  },

  'social-media-age': {
    topic: 'Social Media Age Restrictions',
    task: 'Write ONE body paragraph arguing that the minimum age for using social media platforms should be raised to 18.',
    trialFree: false,
    sections: [
      { label: 'Arguments For Higher Age Limit', color: 'green', items: [
        'Social media algorithms are designed to maximise engagement — harmful to developing minds',
        'Research links heavy social media use in adolescents to anxiety, depression, and poor body image',
        'Young people lack emotional maturity to navigate cyberbullying and predatory behaviour',
        'Platforms collect and monetise children\'s data — a serious privacy violation',
      ]},
      { label: 'Counter-Arguments', color: 'red', items: [
        'Social media connects isolated young people and provides vital community and support networks',
        'Age verification is technically very difficult and easy for teenagers to circumvent',
        'Education about safe online use is more effective than blanket bans',
        'Restricting access may push young people to less regulated, more dangerous platforms',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'UK Online Safety Act (2023): requires platforms to protect children from harmful content',
        'Australia (2024): law banning social media for under-16s passed with strong public support',
        'Jonathan Haidt "The Anxious Generation": evidence linking social media to teen mental health crisis',
        'US research: 40% of children on social media have experienced cyberbullying',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'cyberbullying', 'digital wellbeing', 'online safety', 'screen addiction',
        'mental health', 'data privacy', 'age verification', 'algorithmic manipulation',
      ]},
    ],
  },

  'working-from-home-opinion': {
    topic: 'Working from Home — More Advantages than Disadvantages',
    task: 'Write ONE body paragraph arguing that remote working has more advantages than disadvantages for both employees and employers.',
    trialFree: false,
    sections: [
      { label: 'Advantages of Remote Work', color: 'green', items: [
        'Remote work eliminates commuting time — workers gain hours of productive time each week',
        'Flexibility to manage work around personal and family responsibilities improves work-life balance',
        'Studies show remote workers are often more productive for focused, independent tasks',
        'Companies save significantly on office space and facilities management costs',
      ]},
      { label: 'Disadvantages of Remote Work', color: 'red', items: [
        'Professional isolation can damage team relationships and mental wellbeing',
        'New employees and junior staff particularly need in-person mentorship to develop skills',
        'Home environments are not equally suitable — many workers lack quiet space or equipment',
        'Collaboration and creativity often suffer without spontaneous face-to-face interaction',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Stanford study: remote workers 13% more productive than office counterparts',
        'Microsoft Work Trend Index (2024): 73% of employees want flexible remote options',
        'IBM reversed its remote work policy — found innovation declined without face-to-face work',
        'UAE companies: hybrid models widely adopted post-COVID with positive productivity outcomes',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'flexible working', 'work-life balance', 'hybrid model', 'productivity',
        'professional isolation', 'digital communication', 'office culture', 'commute',
      ]},
    ],
  },

  'remote-working': {
    topic: 'Remote Working — Advantages and Disadvantages',
    task: 'Write ONE body paragraph identifying and explaining TWO clear disadvantages of the trend towards remote working for employees.',
    trialFree: true,
    sections: [
      { label: 'Advantages', color: 'green', items: [
        'Greater flexibility in managing personal and professional responsibilities',
        'Elimination of daily commute — saving time, money, and reducing stress',
        'Higher productivity for focused, individual tasks in quiet home environments',
        'Access to global employment opportunities regardless of location',
      ]},
      { label: 'Disadvantages', color: 'red', items: [
        'Professional isolation can damage team relationships and mental wellbeing',
        'Collaboration and spontaneous creative exchange are harder to replicate online',
        'Home environments may be unsuitable — noise, lack of equipment, childcare responsibilities',
        'Career development and mentorship suffer without in-person interaction with colleagues',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Stanford study: 13% productivity increase among remote workers',
        'Microsoft: digital exhaustion increases significantly with fully remote working',
        'IBM reversed remote work policy — found innovation declined without face-to-face work',
        'UAE companies report lower overhead costs but mixed results on team cohesion',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'flexible working', 'work-life balance', 'digital nomad', 'virtual collaboration',
        'professional isolation', 'productivity', 'hybrid working', 'burnout',
      ]},
    ],
  },

  'artificial-intelligence': {
    topic: 'Artificial Intelligence in the Workplace',
    task: 'Write ONE body paragraph explaining the main disadvantages of using artificial intelligence in the workplace, focusing on the impact on employment.',
    trialFree: false,
    sections: [
      { label: 'Advantages of AI', color: 'green', items: [
        'AI automates repetitive tasks — freeing humans for creative and strategic work',
        'AI-powered tools dramatically increase accuracy and efficiency across industries',
        'Medical AI detects diseases earlier and more accurately than doctors in some cases',
        'AI enables better decision-making through data analysis beyond human capability',
      ]},
      { label: 'Disadvantages of AI', color: 'red', items: [
        'AI is displacing workers in manufacturing, administration, and customer service',
        'The transition creates unemployment and skills gaps that societies struggle to manage',
        'Over-reliance on AI risks reducing human critical thinking and problem-solving',
        'Privacy and data security risks increase as AI processes vast personal information',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'GitHub Copilot: increases software developer productivity by 55% (2023 study)',
        'IMF 2024: 40% of jobs globally exposed to automation by AI',
        'NHS England: AI screening catches 20% more cancers than radiologists alone',
        'EU AI Act (2024): first major regulation limiting high-risk AI uses globally',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'automation', 'machine learning', 'job displacement', 'digital transformation',
        'algorithmic bias', 'data privacy', 'human-AI collaboration', 'reskilling',
      ]},
    ],
  },

  'online-shopping': {
    topic: 'Online Shopping — Advantages and Disadvantages',
    task: 'Write ONE body paragraph explaining the main disadvantages of the rapid growth of online shopping for individuals and society.',
    trialFree: false,
    sections: [
      { label: 'Advantages', color: 'green', items: [
        'Unparalleled convenience — shop 24/7 from anywhere in the world',
        'Access to global markets and far greater product variety than any physical store',
        'Price comparison tools allow consumers to find the best deals instantly',
        'Particularly beneficial for elderly, disabled, or people in remote areas',
      ]},
      { label: 'Disadvantages', color: 'red', items: [
        'Devastating impact on traditional high streets — thousands of physical stores have closed',
        'Massive increase in packaging waste and delivery carbon emissions',
        'Encourages impulsive purchasing and overconsumption — financial harm to individuals',
        'Loss of community spaces and social interactions that physical shopping provides',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Amazon: 300 million active customers — convenience is the primary driver',
        'UAE: e-commerce grew 53% between 2020 and 2024',
        'UK: 17,000 physical retail stores closed between 2018 and 2024',
        'Fast fashion platforms like SHEIN: linked to overconsumption and textile waste crises',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'e-commerce', 'consumer behaviour', 'supply chain', 'digital marketplace',
        'impulse buying', 'carbon footprint', 'last-mile delivery', 'overconsumption',
      ]},
    ],
  },

  'international-tourism': {
    topic: 'International Tourism — Advantages and Disadvantages',
    task: 'Write ONE body paragraph explaining the main advantages of international tourism for developing countries.',
    trialFree: false,
    sections: [
      { label: 'Advantages', color: 'green', items: [
        'Major source of income and employment for developing nations',
        'Promotes cross-cultural understanding, tolerance, and global citizenship',
        'Funds the preservation of historical sites and cultural heritage',
        'Stimulates investment in infrastructure that also benefits local populations',
      ]},
      { label: 'Disadvantages', color: 'red', items: [
        'Overtourism damages fragile ecosystems, historical sites, and local cultures',
        'Dramatic increases in living costs in tourist areas price out local residents',
        'Air travel is a major contributor to carbon emissions and climate change',
        'Tourist-dependent economies are extremely vulnerable to external shocks like pandemics',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Egypt: tourism contributes approximately 12% of GDP — millions of jobs depend on it',
        'Jordan: Petra generates essential income for local Bedouin communities',
        'Venice and Barcelona: residents campaigning against overtourism',
        'COVID-19: tourism-dependent economies in the Caribbean were devastated overnight',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'overtourism', 'sustainable tourism', 'ecotourism', 'cultural heritage',
        'carbon footprint', 'local economy', 'tourist infrastructure', 'mass tourism',
      ]},
    ],
  },

  'traffic-congestion': {
    topic: 'Traffic Congestion — Causes and Solutions',
    task: 'Write ONE body paragraph identifying TWO main causes of traffic congestion in cities and explaining one in detail with a specific example.',
    trialFree: true,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Rapid urban population growth without proportional expansion of road infrastructure',
        'Over-dependence on private car ownership due to inadequate public transport',
        'Poor urban planning — residential and commercial zones placed too far apart',
        'Rising incomes enabling more people to purchase cars in developing countries',
      ]},
      { label: 'Effective Solutions', color: 'orange', items: [
        'Invest in reliable, affordable public transport — metro systems, bus rapid transit, light rail',
        'Introduce congestion charging schemes in city centres to deter private car use',
        'Promote cycling infrastructure, walkable neighbourhoods, and car-sharing schemes',
        'Implement flexible working hours and remote work policies to reduce peak-hour demand',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Singapore ERP congestion pricing: reduced central city traffic by 25% since 1998',
        'London congestion charge: reduced traffic in central London by 30% within first year',
        'Amsterdam: 800,000 daily bicycle journeys — infrastructure investment transformed mobility',
        'Cairo: 20 million residents — among world\'s worst traffic, 3+ hour daily commutes',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'congestion charging', 'public transport', 'urban planning', 'carbon emissions',
        'cycling infrastructure', 'car-sharing', 'peak hours', 'road infrastructure',
      ]},
    ],
  },

  'youth-unemployment': {
    topic: 'Youth Unemployment — Causes and Solutions',
    task: 'Write ONE body paragraph proposing TWO concrete solutions to youth unemployment and explaining how one would work in practice.',
    trialFree: false,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Education systems not aligned with labour market needs — graduates lack practical skills',
        'Automation and AI displacing entry-level positions traditionally filled by young workers',
        'Economic downturns reduce hiring — young workers are the first to be cut',
        'Employers require experience — but young people need employment to gain experience',
      ]},
      { label: 'Effective Solutions', color: 'orange', items: [
        'Expand vocational training and apprenticeship programmes aligned with industry needs',
        'Offer employers tax incentives for hiring and training young workers',
        'Reform university curricula to include practical skills, internships, and industry partnerships',
        'Support youth entrepreneurship through micro-financing, mentorship, and reduced barriers',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Germany dual system: 50% of school leavers enter apprenticeships — youth unemployment under 6%',
        'MENA region: youth unemployment rate of 26% — highest globally (ILO 2024)',
        'UAE Emiratisation programme: government incentives for private sector youth hiring',
        'Kenya: youth enterprise fund provides micro-loans — created 500,000+ jobs',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'vocational training', 'skills mismatch', 'apprenticeship', 'entrepreneurship',
        'graduate underemployment', 'labour market', 'youth empowerment', 'automation',
      ]},
    ],
  },

  'plastic-pollution': {
    topic: 'Plastic Pollution — Causes and Solutions',
    task: 'Write ONE body paragraph arguing that governments bear primary responsibility for solving plastic pollution, not individuals.',
    trialFree: false,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Single-use plastic packaging dominates consumer goods — convenience prioritised over sustainability',
        'Inadequate waste management infrastructure in many developing countries',
        'Consumer culture of disposal — low-cost plastic makes recycling seem unnecessary',
        'Lack of viable affordable alternatives to plastic in food packaging',
      ]},
      { label: 'Effective Solutions', color: 'orange', items: [
        'Governments should ban or heavily tax single-use plastics and incentivise alternatives',
        'Invest in national recycling infrastructure — collection, sorting, and processing facilities',
        'International cooperation needed — plastic pollution crosses national borders',
        'Individuals should reduce plastic consumption and choose reusable alternatives',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Kenya plastic bag ban (2017): most comprehensive in Africa — significant litter reduction',
        'EU Single-Use Plastics Directive (2021): banned 10 common plastic items',
        '8 million tonnes of plastic enter the oceans every year (UNEP 2024)',
        'The Ocean Cleanup project: removed over 220,000 kg from the Great Pacific Garbage Patch',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'single-use plastics', 'microplastics', 'recycling infrastructure', 'biodegradable',
        'circular economy', 'marine pollution', 'extended producer responsibility', 'sustainability',
      ]},
    ],
  },

  'loneliness-in-society': {
    topic: 'Loneliness in Modern Society',
    task: 'Write ONE body paragraph identifying the main causes of loneliness in modern society and explaining one cause in detail.',
    trialFree: false,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Urbanisation and geographic mobility breaking down traditional community bonds',
        'Social media creating the illusion of connection while replacing meaningful real interaction',
        'Rising single-person households and decline of extended family living arrangements',
        'Long working hours and smartphone culture reducing time for genuine socialising',
      ]},
      { label: 'Effective Solutions', color: 'orange', items: [
        'Urban planning should prioritise community spaces — parks, libraries, community centres',
        'Employers should encourage social connection and flexible hours for community involvement',
        'Social media literacy education to help people use technology for genuine connection',
        'Community programmes targeting vulnerable groups — elderly, new immigrants, single parents',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'UK: first Minister for Loneliness appointed in 2018 — 9 million people often or always lonely',
        'Japan: hikikomori — over 1 million people living in total social isolation',
        'USA Surgeon General: declared loneliness a public health epidemic in 2023',
        'Denmark: community housing model (bofællesskaber) reduces isolation significantly',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'social isolation', 'community cohesion', 'mental wellbeing', 'social prescribing',
        'digital connection', 'urban loneliness', 'social infrastructure', 'hikikomori',
      ]},
    ],
  },

  'ageing-population': {
    topic: 'Ageing Population — Causes and Consequences',
    task: 'Write ONE body paragraph explaining the main consequences of an ageing population for the economy and public services.',
    trialFree: false,
    sections: [
      { label: 'Why It Is Happening', color: 'blue', items: [
        'Advances in medical science have dramatically extended life expectancy worldwide',
        'Improved nutrition, sanitation, and living standards reduce mortality at all ages',
        'Declining birth rates mean younger generations are proportionally smaller',
        'Better treatment of chronic diseases allows people to live longer with manageable conditions',
      ]},
      { label: 'Consequences for Society', color: 'red', items: [
        'Pressure on pension systems and healthcare budgets as retired population grows',
        'Labour shortages in key sectors as working-age population declines',
        'Increased demand for elderly care services, housing, and specialised healthcare',
        'Intergenerational tension over resource allocation and social priorities',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Japan: life expectancy of 84 — world\'s highest — result of healthcare investment',
        'Germany: importing skilled workers from abroad to offset declining native workforce',
        'South Korea: pension fund projected to be depleted by 2055 if current trends continue',
        'UK NHS: estimated to need 488,000 additional workers by 2030',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'demographic shift', 'dependency ratio', 'pension fund', 'healthcare burden',
        'silver economy', 'retirement age', 'intergenerational equity', 'fertility rate',
      ]},
    ],
  },

  'children-outdoor-play': {
    topic: 'Children and Outdoor Play',
    task: 'Write ONE body paragraph explaining the main effects of reduced outdoor play on children\'s physical and mental health.',
    trialFree: false,
    sections: [
      { label: 'Why Children Play Less Outdoors', color: 'blue', items: [
        'Digital devices — smartphones, tablets, gaming consoles — compete with outdoor activities',
        'Parental safety concerns about traffic, strangers, and unsupervised play have increased',
        'Academic pressure and homework demands leave children less free time',
        'Urban environments provide fewer safe, accessible green spaces for children',
      ]},
      { label: 'Negative Effects', color: 'red', items: [
        'Physical health: rising childhood obesity, vitamin D deficiency, and poor motor skills',
        'Mental health: increased anxiety, reduced resilience, and attention difficulties',
        'Social consequences: children developing fewer conflict resolution and interpersonal skills',
        'Long-term: less environmentally aware generation with weaker connection to nature',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'WHO: childhood obesity has tripled globally since 1975 — sedentary lifestyle is key driver',
        'Finland: mandatory outdoor breaks every 45 minutes — among world\'s highest academic performers',
        'UK survey: children spend average 7 hours daily on screens',
        'Australia: school nature programmes reduced ADHD symptoms significantly in clinical trials',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'sedentary lifestyle', 'nature deficit disorder', 'childhood obesity', 'screen time',
        'unstructured play', 'motor development', 'digital childhood', 'resilience',
      ]},
    ],
  },

  'space-exploration': {
    topic: 'Space Exploration — Is It Worth the Cost?',
    task: 'Write ONE body paragraph arguing that the enormous financial investment in space exploration is justified by its practical and scientific benefits.',
    trialFree: false,
    sections: [
      { label: 'Arguments For Space Exploration', color: 'green', items: [
        'Every $1 invested in space research returns approximately $7 to the economy',
        'Technological spin-offs: GPS, memory foam, water filtration — all developed through space research',
        'Earth observation satellites essential for monitoring climate change and natural disasters',
        'International space cooperation promotes diplomatic relationships between nations',
      ]},
      { label: 'Arguments Against Space Spending', color: 'red', items: [
        'Enormous financial investment could address poverty, disease, and climate change on Earth',
        'UNICEF estimates $30 billion per year could end world hunger entirely',
        'Benefits mainly accrue to wealthy nations who can afford space programmes',
        'The ISS cost $150 billion — that money could have transformed healthcare globally',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'UAE Hope Probe (2021): positioned UAE as a scientific leader in the Arab world',
        'SpaceX Starlink: satellite internet providing connectivity to remote areas globally',
        'NASA budget: $25 billion annually — returns significant economic value through spin-off technology',
        'International Space Station: produced thousands of scientific studies and breakthroughs',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'space exploration', 'technological spin-offs', 'astronomical investment', 'cosmic research',
        'satellite technology', 'national prestige', 'interplanetary', 'scientific advancement',
      ]},
    ],
  },

  'fast-food': {
    topic: 'Fast Food — Popularity and Problems',
    task: 'Write ONE body paragraph explaining the main health and social problems that the rise of fast food has created.',
    trialFree: false,
    sections: [
      { label: 'Why Fast Food Is So Popular', color: 'blue', items: [
        'Modern lifestyles are faster and more time-pressured — convenience is the primary appeal',
        'Fast food is deliberately engineered to be addictive through sugar, salt, and fat',
        'Aggressive global marketing, especially targeting children, drives demand',
        'Low cost makes fast food accessible to people who cannot afford healthier alternatives',
      ]},
      { label: 'Problems Created', color: 'red', items: [
        'Obesity epidemic: high calorie, low nutrition food linked directly to rising global obesity',
        'Displacement of traditional food cultures and family meal traditions',
        'Health system burden: obesity-related diseases cost healthcare systems trillions annually',
        'Environmental impact: packaging, meat production, and waste contribute to degradation',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'McDonald\'s: serves 70 million customers daily in 120 countries',
        'WHO: 1 in 8 people globally obese in 2024 — fast food is a major contributing factor',
        'Gulf region: among world\'s highest rates of diabetes and obesity — diet is primary driver',
        'UK: 70% of hospital admissions linked to obesity-related conditions — annual cost £6.5 billion',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'processed food', 'nutritional value', 'food desert', 'obesity epidemic',
        'consumer culture', 'fast food industry', 'dietary habits', 'public health',
      ]},
    ],
  },

  'advertising': {
    topic: 'Advertising — Advantages and Disadvantages',
    task: 'Write ONE body paragraph arguing that advertising has a harmful effect on society, using at least ONE specific example.',
    trialFree: true,
    sections: [
      { label: 'Problems with Advertising', color: 'red', items: [
        'Advertising manipulates people into buying things they do not need',
        'It promotes a harmful consumer culture focused on material possessions',
        'Advertising aimed at children is particularly unethical and exploitative',
        'It creates unrealistic body images and promotes unhealthy lifestyle expectations',
      ]},
      { label: 'Benefits of Advertising', color: 'green', items: [
        'Companies need to inform people about their products and services',
        'Advertising creates jobs and supports an entire creative industry',
        'Without advertising there would be less consumer choice and higher prices',
        'Advertising funds free media content including television and online services',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Super Bowl commercials: $7 million for 30 seconds — shows advertising power over consumers',
        'UK: banned junk food advertising before 9pm to protect children',
        'Social media advertising: allows precise targeting by age, location, and interests',
        'Egypt and Gulf: advertising industry growing rapidly with digital platforms',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'consumer culture', 'brand image', 'manipulation', 'regulation',
        'marketing campaigns', 'target audience', 'subliminal advertising', 'commercialism',
      ]},
    ],
  },

  'water-scarcity': {
    topic: 'Water Scarcity — UAE and Middle East',
    task: 'Write ONE body paragraph proposing TWO effective solutions to water scarcity in the Middle East, explaining one solution in detail.',
    trialFree: false,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Arid climate with extremely low natural rainfall across most of the region',
        'Rapidly growing urban populations increasing demand for fresh water',
        'Agricultural irrigation consumes approximately 70% of all fresh water globally',
        'Climate change is intensifying droughts and reducing precipitation',
      ]},
      { label: 'Effective Solutions', color: 'orange', items: [
        'Desalination technology converts sea water to drinking water — widely used in UAE',
        'Water recycling and wastewater treatment for agricultural and industrial use',
        'Drip irrigation and precision farming dramatically reduce agricultural water use',
        'Public awareness campaigns and pricing structures to discourage water waste',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'UAE: one of the world\'s highest per capita water consumers — 550 litres per day',
        'Saudi Arabia: world\'s largest desalination capacity',
        'Israel: 85% of wastewater is recycled — world leader in water reuse',
        'Qatar: investing heavily in desalination ahead of rapid population growth',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'desalination', 'water conservation', 'groundwater depletion', 'irrigation',
        'water stress', 'recycled water', 'aquifer', 'water security',
      ]},
    ],
  },

  'electric-vehicles': {
    topic: 'Electric Vehicles — Advantages and Disadvantages',
    task: 'Write ONE body paragraph arguing that electric vehicles are the most important solution to reducing carbon emissions from transport.',
    trialFree: false,
    sections: [
      { label: 'Advantages of EVs', color: 'green', items: [
        'Zero direct emissions reduce urban air pollution significantly',
        'Lower running costs — electricity is cheaper than petrol for equivalent distance',
        'Reduced dependence on fossil fuels and exposure to oil price volatility',
        'Rapidly improving battery technology is extending range and reducing charging time',
      ]},
      { label: 'Disadvantages of EVs', color: 'red', items: [
        'High upfront purchase cost compared to conventional vehicles',
        'Charging infrastructure is still underdeveloped in many countries',
        'Battery production has significant environmental costs — mining lithium and cobalt',
        'Range anxiety remains a concern for long-distance drivers',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Norway: 80% of new car sales are electric — world leader in EV adoption',
        'UAE: government investing heavily in EV charging infrastructure across the country',
        'Tesla: made electric vehicles aspirational and mainstream globally',
        'China: world\'s largest EV market — manufacturing and adoption both growing rapidly',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'zero emissions', 'carbon footprint', 'charging infrastructure', 'battery technology',
        'range anxiety', 'lithium mining', 'energy transition', 'decarbonisation',
      ]},
    ],
  },

  'animal-testing': {
    topic: 'Animal Testing — Is It Justified?',
    task: 'Write ONE body paragraph arguing that animal testing for medical research is morally unacceptable and should be banned.',
    trialFree: false,
    sections: [
      { label: 'Arguments Against Animal Testing', color: 'red', items: [
        'The benefits of research do not justify the suffering caused to animals',
        'Computer simulation can replace animal testing in most cases today',
        'Animals have rights — humans have no moral authority to experiment on them',
        'Alternative research methods are increasingly effective and more reliable',
      ]},
      { label: 'Arguments For Animal Testing', color: 'green', items: [
        'Animals are used in important scientific research that saves human lives',
        'Medical testing on animals is necessary to develop safe new drugs',
        'Animal testing has helped advance medical and scientific knowledge enormously',
        'Some medical experiments simply cannot ethically be conducted on humans',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'EU: cosmetics testing on animals now banned across all member states',
        'Many important medical discoveries involved early animal experimentation',
        'Computer modelling is increasingly replacing animal tests in drug development',
        'PETA campaigns: led major cosmetics companies to abandon animal testing globally',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'vivisection', 'alternative testing methods', 'ethical research', 'animal welfare',
        'pharmaceutical testing', 'computer simulation', 'cruelty-free', 'bioethics',
      ]},
    ],
  },

  'brain-drain': {
    topic: 'Brain Drain — Causes and Consequences',
    task: 'Write ONE body paragraph explaining the consequences of brain drain for developing countries that lose highly educated citizens.',
    trialFree: false,
    sections: [
      { label: 'Main Causes', color: 'blue', items: [
        'Higher salaries and better career opportunities in developed countries',
        'Better research facilities, technology, and infrastructure abroad',
        'Political instability, conflict, or lack of opportunities in home countries',
        'Quality of life factors — safety, education for children, better healthcare',
      ]},
      { label: 'Consequences', color: 'red', items: [
        'Home countries lose their most educated and skilled workers permanently',
        'Healthcare systems suffer — doctors and nurses migrate in large numbers',
        'Economic development is slowed as talent and innovation drain away',
        'Long-term, remittances sent home by migrants can partially benefit home economies',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'MENA region: significant medical talent emigration to Gulf countries and Europe',
        'Egypt: thousands of engineers and doctors working abroad in the Gulf and Europe',
        'India: tech professionals in Silicon Valley send billions in remittances annually',
        'UAE and Saudi Arabia: creating incentives to attract and retain regional talent',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'skilled migration', 'remittances', 'human capital', 'knowledge economy',
        'talent gap', 'reverse brain drain', 'diaspora', 'intellectual exodus',
      ]},
    ],
  },

  'online-learning': {
    topic: 'Online Learning — Advantages and Disadvantages',
    task: 'Write ONE body paragraph explaining the main advantages of online learning for students in developing countries.',
    trialFree: false,
    sections: [
      { label: 'Advantages', color: 'green', items: [
        'Accessible from anywhere — removes geographical and physical barriers to education',
        'Self-paced learning allows students to study at their own speed and convenience',
        'Often far more affordable than traditional face-to-face education',
        'Wide variety of courses available from world-leading universities and institutions',
      ]},
      { label: 'Disadvantages', color: 'red', items: [
        'Lack of face-to-face interaction reduces social development and communication skills',
        'Requires strong self-discipline — many students struggle without structure',
        'Practical skills and laboratory work cannot be replicated online',
        'Digital divide means many students lack reliable internet access or devices',
      ]},
      { label: 'Real Examples', color: 'teal', items: [
        'Coursera and edX: millions access top university courses for free or low cost',
        'COVID-19: forced global experiment in online learning — mixed but revealing results',
        'UAE: strong digital infrastructure makes online learning highly accessible',
        'Rural Egypt: online learning provides access to quality education previously unavailable',
      ]},
      { label: 'Key Vocabulary', color: 'purple', items: [
        'e-learning', 'blended learning', 'MOOCs', 'self-directed learning',
        'digital access', 'virtual classroom', 'academic integrity', 'digital divide',
      ]},
    ],
  },

};

// The 3 topics that are free for trial users
export const TRIAL_FREE_TOPICS = ['advertising', 'traffic-congestion', 'remote-working'];
